import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Brain, Heart, Shield, ArrowRight, CheckCircle2, Phone, Mail, PhoneCall,
  MessageCircle, Sparkles, TrendingUp, Quote, Star
} from 'lucide-react';

import { SiteHeader } from '../components/sections/SiteHeader';
import { SiteFooter } from '../components/sections/SiteFooter';
import { FAQSection } from '../components/sections/FAQSection';
import { PricingStrip } from '../components/sections/PricingStrip';
import { LeadCaptureSection } from '../components/sections/LeadCaptureSection';

const areasOfFocus = [
  {
    icon: Brain,
    title: 'Anxiety',
    description: 'Moving past chronic worry and nervous system overwhelm.',
    color: 'text-emerald-700',
    href: '/anxiety'
  },
  {
    icon: Shield,
    title: 'OCD',
    description: 'Breaking free from intrusive thoughts and compulsive behavioral loops.',
    color: 'text-emerald-600',
    href: '/ocd'
  },
  {
    icon: Heart,
    title: 'Stress Management',
    description: 'Building cognitive resilience for Work Stress, Relationships, and Emotional Regulation — across burnout and life transitions.',
    color: 'text-emerald-800',
    href: '/stress'
  }
];

const usps = [
  {
    title: 'Active, Not Silent',
    desc: "I don't sit silently across from you. Our sessions are structured, evidence-based, and active — we explore patterns together, not in monologues."
  },
  {
    title: 'You Leave With a Tool',
    desc: 'Every session ends with a concrete CBT/ERP technique you can practice outside the room. No vague homework, no fluff.'
  },
  {
    title: 'You Are Not Alone for 6 Days',
    desc: "Reasonable check-ins between sessions (within healthy boundaries) so you don't lose momentum between weekly meetings."
  }
];

const journeySteps = [
  {
    icon: PhoneCall,
    step: '01',
    title: 'The Initial Connect',
    desc: "After you book, I will personally reach out via a brief call or text message to understand your immediate concerns and clearly explain how our therapeutic alliance works."
  },
  {
    icon: MessageCircle,
    step: '02',
    title: 'The Deep Dive Session',
    desc: "Our active, structured 60-minute session. We drop the clinical jargon to explore your actual cognitive loops, behavioural patterns, and emotional blocks in a completely safe space."
  },
  {
    icon: Sparkles,
    step: '03',
    title: 'The Practical Toolkit',
    desc: "You don't just vent; you walk away with targeted, actionable psychological strategies (CBT/ERP) to actively practice outside the therapy room."
  },
  {
    icon: TrendingUp,
    step: '04',
    title: 'Maintenance & Growth (If Required)',
    desc: 'Ongoing pacing and strategic check-ins designed to consolidate your progress, prevent relapses, and ensure you become your own primary healer.'
  }
];

const testimonials = [
  {
    initials: 'K.B.',
    text: "Such a beautiful session with Ayesha mam. She listened to me with so much patience. Talking to her is such a big relief. Highly recommend to everyone — at least take the initiative, OCD warriors."
  },
  {
    initials: 'S.J.',
    text: "Dear Ayesha — thank you for creating a safe and supportive space for me to explore my thoughts and emotions. Your guidance has been invaluable. You're an exceptional psychologist and an even more exceptional human being."
  },
  {
    initials: 'D.S.',
    text: "It was a happy 1st session with Ayesha ma'am."
  }
];

const Home = () => {
  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-7 text-center lg:text-left order-2 lg:order-1">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight">
                  Therapy is Not a Sign of Weakness,<br />
                  <span className="text-primary">It's a Step Towards Healing</span>
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                  A safe, structured, and non-judgmental space to navigate your storms, reframe negative cycles, and reclaim your inner peace.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start items-center">
                <Link to="/services?service=individual">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-base md:text-lg px-7 py-6 w-full sm:w-auto" data-testid="hero-book-session-btn">
                    Book a 60-Minute Session
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services?service=discovery">
                  <Button size="lg" variant="outline" className="text-base md:text-lg px-7 py-6 border-primary text-primary hover:bg-primary/10 bg-transparent w-full sm:w-auto" data-testid="hero-discovery-call-btn">
                    20-Min Discovery Call (Rs. 250)
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-muted-foreground italic" data-testid="hero-cta-microcopy">
                Takes less than 2 minutes. I will personally respond to your inquiry via your preferred contact method before the session.
              </p>

              <div className="flex flex-wrap gap-3 justify-center lg:justify-start pt-2">
                <Badge variant="secondary" className="px-3 py-1.5 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Safe
                </Badge>
                <Badge variant="secondary" className="px-3 py-1.5 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Compassionate
                </Badge>
                <Badge variant="secondary" className="px-3 py-1.5 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Confidential
                </Badge>
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative max-w-md w-full">
                <div className="aspect-[4/5] bg-muted rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/ivjgnyan_Ashhhhh.jpg"
                    alt="Ayesha Khaleel, M.A."
                    className="w-full h-full object-cover"
                    data-testid="hero-photo"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 bg-card border-2 border-primary/20 px-5 py-3 rounded-xl shadow-lg hidden sm:block">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Practising</p>
                  <p className="text-sm font-serif font-semibold text-foreground">Ayesha Khaleel, M.A.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Ways to Begin Strip — balanced framing */}
      <section className="bg-primary/10 border-y border-primary/20 py-5">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-4 sm:gap-8 items-center">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-foreground leading-relaxed">
                <strong>Ready to begin therapy?</strong> Book a full <strong>60-minute session</strong> and start your work right away.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-foreground leading-relaxed">
                <strong>Prefer to meet first?</strong> A <strong>20-minute Discovery Call (₹250)</strong> is available if you'd like to explore fit before committing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Me */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                    Meet Ayesha Khaleel
                  </h2>
                  <p className="text-lg text-primary font-medium">
                    M.A. in Clinical Psychology
                  </p>
                </div>

                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    With over 2 years of dedicated clinical experience and having supported more than 200+ individuals across multiple therapy centers, I bring a compassionate, integrative approach to mental health care.
                  </p>
                  <p>
                    My practice is grounded in <span className="font-semibold text-foreground">Cognitive Behavioral Therapy (CBT)</span>, specializing in helping clients navigate anxiety, OCD, and stress management. I believe in creating a safe, non-judgmental listening space where we work together to reframe negative thought patterns and break vicious cognitive cycles.
                  </p>
                  <p className="text-foreground font-medium italic border-l-4 border-primary pl-4">
                    "You don't have to face it alone. I'm here to walk with you on your journey from survival to thriving."
                  </p>
                </div>

                <Link to="/services">
                  <Button className="bg-primary hover:bg-primary/90 mt-4">
                    Start Your Healing Journey
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              <div className="relative">
                <div className="aspect-[3/4] bg-muted rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/ivjgnyan_Ashhhhh.jpg"
                    alt="Ayesha Khaleel"
                    className="w-full h-full object-cover"
                    data-testid="ayesha-headshot"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Focus */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                My Areas of Focus
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Specialized support for the challenges that matter most to you
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {areasOfFocus.map((area, index) => (
                <Link to={area.href} key={index} data-testid={`area-link-${area.title.toLowerCase().replace(' ', '-')}`}>
                  <Card className="card-hover border-2 h-full cursor-pointer group">
                    <CardHeader>
                      <div className="w-16 h-16 rounded-full bg-secondary/30 flex items-center justify-center mb-4">
                        <area.icon className={`w-8 h-8 ${area.color}`} />
                      </div>
                      <CardTitle className="text-2xl font-serif flex items-center justify-between">
                        {area.title}
                        <ArrowRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base leading-relaxed">
                        {area.description}
                      </CardDescription>
                      <span className="text-sm text-primary font-medium mt-4 inline-block">
                        Learn more →
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How I Work Differently — USP */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center space-y-3 mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                How I Work Differently
              </h2>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                Many therapists have the same credentials. Here's what sets our sessions apart.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {usps.map((u, i) => (
                <Card key={i} className="card-hover border-2 bg-card" data-testid={`usp-card-${i + 1}`}>
                  <CardContent className="pt-6 space-y-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Star className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-serif font-semibold text-foreground">{u.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{u.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Individual Therapy Journey */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-14">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                Your Individual Therapy Journey
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A clear, structured path from your first message to long-term growth.
              </p>
            </div>

            <div className="relative">
              <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-px bg-primary/20" aria-hidden="true" />
              <div className="grid md:grid-cols-4 gap-8 relative">
                {journeySteps.map((s, i) => (
                  <div key={i} className="relative bg-card" data-testid={`journey-step-${i + 1}`}>
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="relative">
                        <div className="w-24 h-24 rounded-full bg-background border-2 border-primary/30 flex items-center justify-center shadow-sm">
                          <s.icon className="w-9 h-9 text-primary" />
                        </div>
                        <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                          {s.step}
                        </span>
                      </div>
                      <h3 className="text-xl font-serif font-semibold text-foreground">
                        {s.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Efficacy & Client Feedback */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-3 mb-12">
              <div className="inline-block">
                <Quote className="w-8 h-8 text-primary/40 mx-auto" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                Clinical Efficacy & Client Feedback
              </h2>
              <p className="text-base text-muted-foreground max-w-3xl mx-auto italic leading-relaxed">
                Client reflections from Ayesha's prior clinical tenure in institutional healthcare settings:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Card key={i} className="card-hover relative bg-card border-2 border-secondary/40" data-testid={`testimonial-${i + 1}`}>
                  <CardContent className="pt-8 pb-6 space-y-5">
                    <Quote className="w-7 h-7 text-primary/30 absolute top-4 right-4" aria-hidden="true" />
                    <p className="text-sm text-foreground leading-relaxed">
                      {t.text}
                    </p>
                    <div className="flex items-center gap-3 pt-2 border-t border-border/50">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-serif font-semibold text-primary">{t.initials}</span>
                      </div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">
                        Client Reflection
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-8 italic max-w-2xl mx-auto">
              Identities are abbreviated to initials to protect client confidentiality.
            </p>
          </div>
        </div>
      </section>

      <PricingStrip />
      <LeadCaptureSection />
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-serif font-bold">
              Ready to Take the First Step?
            </h2>
            <p className="text-lg opacity-90">
              Invest in your mental health today, for a better tomorrow.
            </p>
            <Link to="/services">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-6 bg-background text-primary hover:bg-background/90">
                View Services & Book Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact for Queries */}
      <section id="contact" className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
                Contact for Queries
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Have a question before booking? Reach out — I'm happy to help.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-hover border-2" data-testid="contact-phone-card">
                <CardHeader>
                  <div className="w-14 h-14 rounded-full bg-secondary/40 flex items-center justify-center mb-3">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-serif">Phone</CardTitle>
                  <CardDescription className="text-base">
                    Call or WhatsApp for queries about therapy, scheduling, or fees.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <a
                    href="tel:+918073769515"
                    className="text-xl font-semibold text-primary hover:underline"
                    data-testid="contact-phone-link"
                  >
                    +91 80737 69515
                  </a>
                  <p className="text-xs text-muted-foreground mt-2">
                    Mon–Sat, 10 AM – 7 PM IST
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover border-2" data-testid="contact-email-card">
                <CardHeader>
                  <div className="w-14 h-14 rounded-full bg-secondary/40 flex items-center justify-center mb-3">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-serif">Email</CardTitle>
                  <CardDescription className="text-base">
                    For longer queries, intake details, or confidential concerns.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <a
                    href="mailto:ayesha.k.psychologist@gmail.com"
                    className="text-base font-semibold text-primary hover:underline break-all"
                    data-testid="contact-email-link"
                  >
                    ayesha.k.psychologist@gmail.com
                  </a>
                  <p className="text-xs text-muted-foreground mt-2">
                    Responses within 24 hours
                  </p>
                </CardContent>
              </Card>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-8">
              All conversations are kept strictly confidential.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Home;
